import React from 'react';
import type {
  ChatDagNodeData,
  PeerDagData,
  ToolCallDagData,
  LlmRoundData,
  TurnMarkerData,
} from '../types';
import { useTheme } from '../../../../contexts/ThemeContext';
import { MarkdownPreview } from '../MarkdownPreview';
import { PIP_HEADER_CLASS } from '../chatDagTokens';

// Per-node hovercard content.
//
// One component, dispatched on the node data's `kind` discriminant.
// Mirrors what's useful to an operator eyeing the DAG: tool call args
// + result, peer prompt/response + token usage/cost, round model, full
// turn marker text + telemetry.
//
// Fits inside the HoverCard primitive's 280px box — body scrollable
// where content can grow (prompts, responses, args). Sections are
// sparse and monospace where bytes matter (token/cost readouts),
// prose-styled where the LLM content is.

function fmtCost(usd?: number | null): string | null {
  if (usd == null) return null;
  if (usd === 0) return '$0';
  if (usd < 0.0001) return `$${usd.toFixed(6)}`;
  if (usd < 0.01) return `$${usd.toFixed(5)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

function fmtDuration(ms?: number | null): string | null {
  if (ms == null) return null;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;
}

function fmtBytes(n?: number | null): string | null {
  if (n == null) return null;
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

function shortenModel(modelId?: string): string | null {
  if (!modelId) return null;
  const tail = modelId.split('/').pop() ?? modelId;
  return tail.replace(/-\d{8}$/, '');
}

const Section: React.FC<{
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  darkMode: boolean;
}> = ({ label, children, mono, darkMode }) => (
  <div>
    <div
      className={`${PIP_HEADER_CLASS} mb-0.5 ${
        darkMode ? 'text-gray-400' : 'text-gray-500'
      }`}
    >
      {label}
    </div>
    <div
      className={`text-[11px] leading-snug ${mono ? 'font-mono' : ''} ${
        darkMode ? 'text-gray-200' : 'text-gray-800'
      }`}
    >
      {children}
    </div>
  </div>
);

const Preview: React.FC<{ text: string; maxLines?: number }> = ({
  text,
  maxLines = 6,
}) => (
  <div
    className="whitespace-pre-wrap break-words"
    style={{
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    }}
  >
    {text}
  </div>
);

// MarkdownPreview now lives in ../MarkdownPreview so the synthesis
// llm_round node can reuse the same renderer for its at-a-glance
// answer preview. Imported above.

// Render a friendly label for the consult_peers fuzzy resolver path
// (matched_via). Backend tier names are concise but cryptic — ladder
// them to one-word descriptions.
function describeMatchedVia(via?: string): string | null {
  if (!via) return null;
  switch (via) {
    case 'uuid_exact':
      return 'UUID match';
    case 'name_exact':
      return 'name match';
    case 'name_substring':
      return 'name fragment';
    case 'name_token':
      return 'name token';
    case 'tag':
    case 'tag_token':
      return 'tag match';
    case 'specialization':
    case 'specialization_token':
      return 'specialization';
    default:
      return via;
  }
}

const PeerHover: React.FC<{ data: PeerDagData; darkMode: boolean }> = ({
  data,
  darkMode,
}) => {
  const name = data.personaName ?? 'Unknown peer';
  const model = shortenModel(data.model);
  const duration = fmtDuration(data.durationMs);
  const cost = fmtCost(data.costUsd);
  const hasStats =
    data.totalTokens != null || data.costUsd != null || data.durationMs != null;
  const isError = data.status === 'error';

  // Match info — answers "why was this specific peer picked?" — useful
  // when the LLM passed free-text queries instead of UUIDs and the
  // fuzzy resolver did the matching. Compact one-line form below the
  // name/id block.
  const matchedViaLabel = describeMatchedVia(data.matchedVia);
  const matchQuery = data.matchQueries?.[0];
  const showMatchInfo =
    matchedViaLabel || matchQuery || (data.candidateCount ?? 0) > 1;

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="font-bold text-[13px] leading-tight">
          {name.replace(/_/g, ' ')}
        </div>
        {data.personaId && (
          <div
            className={`font-mono text-[9px] truncate mt-0.5 ${
              darkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
            title={data.personaId}
          >
            {data.personaId}
          </div>
        )}
        {showMatchInfo && (
          <div
            className={`text-[10px] mt-1 leading-tight ${
              darkMode ? 'text-emerald-300/80' : 'text-emerald-700/80'
            }`}
          >
            {matchQuery && (
              <>
                via <span className="font-mono">"{matchQuery}"</span>
                {data.matchQueries && data.matchQueries.length > 1 && (
                  <span className="opacity-70">
                    {' '}
                    +{data.matchQueries.length - 1} more
                  </span>
                )}
              </>
            )}
            {matchedViaLabel && (
              <>
                {matchQuery ? ' · ' : ''}
                <span className="opacity-90">{matchedViaLabel}</span>
                {data.matchedTag && (
                  <span className="opacity-70">
                    {' '}
                    (<span className="font-mono">{data.matchedTag}</span>)
                  </span>
                )}
              </>
            )}
            {(data.candidateCount ?? 0) > 1 && (
              <span className="opacity-70">
                {' '}
                · {data.candidateCount} candidates
              </span>
            )}
          </div>
        )}
      </div>

      {model && (
        <Section label="Model" mono darkMode={darkMode}>
          {model}
        </Section>
      )}

      {hasStats && (
        <div className="flex items-center gap-3 text-[10px] font-mono opacity-90">
          {data.totalTokens != null && (
            <span title={`in ${data.promptTokens ?? 0} · out ${data.completionTokens ?? 0}`}>
              {data.totalTokens.toLocaleString()} tok
            </span>
          )}
          {cost && <span>💰 {cost}</span>}
          {duration && <span>⏱ {duration}</span>}
        </div>
      )}

      {isError && data.error && (
        <Section label="Error" darkMode={darkMode}>
          <span className={darkMode ? 'text-rose-300' : 'text-rose-700'}>
            {data.error}
          </span>
        </Section>
      )}

      {data.context && (
        <Section label="Context sent" darkMode={darkMode}>
          <MarkdownPreview text={data.context} maxLines={4} />
        </Section>
      )}

      {data.prompt && (
        <Section label="Prompt sent" darkMode={darkMode}>
          <MarkdownPreview text={data.prompt} maxLines={6} />
        </Section>
      )}

      {data.response && (
        <Section label="Response" darkMode={darkMode}>
          <MarkdownPreview text={data.response} maxLines={12} />
        </Section>
      )}
    </div>
  );
};

const ToolCallHover: React.FC<{
  data: ToolCallDagData;
  darkMode: boolean;
}> = ({ data, darkMode }) => {
  const call = data.call;
  const toolName = call.identity?.tool_name ?? call.tool_name ?? 'tool';
  const status = call.outcome?.status ?? 'ok';
  const duration = fmtDuration(call.timing?.duration_ms);
  const argsBytes = fmtBytes((call as any).args_bytes);
  const resultBytes = fmtBytes((call as any).result_bytes);
  const isError = status !== 'ok';
  const errMsg = call.outcome?.error_message;
  const args = call.arguments ?? {};
  const argsKeys = Object.keys(args);

  // Result extraction — tools often return `{success: true, ...}`,
  // so surface the top-level success/error + a few readable fields.
  const result: any = (call as any).result ?? {};
  const resultSuccess = result?.success;
  const resultError = result?.error;

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="font-bold text-[13px] leading-tight font-mono">
          {toolName}
        </div>
        <div
          className={`text-[9px] mt-0.5 ${
            darkMode ? 'text-gray-500' : 'text-gray-400'
          }`}
        >
          round {data.round} · {status}
          {duration ? ` · ${duration}` : ''}
        </div>
      </div>

      {(argsBytes || resultBytes) && (
        <div className="flex items-center gap-3 text-[10px] font-mono opacity-80">
          {argsBytes && <span>args {argsBytes}</span>}
          {resultBytes && <span>result {resultBytes}</span>}
        </div>
      )}

      {isError && errMsg && (
        <Section label="Error" darkMode={darkMode}>
          <span className={darkMode ? 'text-rose-300' : 'text-rose-700'}>
            {errMsg}
          </span>
        </Section>
      )}

      {argsKeys.length > 0 && (
        <Section label="Arguments" mono darkMode={darkMode}>
          <div
            className="whitespace-pre-wrap break-words"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 6,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {argsKeys.map((k) => {
              const v = args[k];
              const vStr =
                typeof v === 'string'
                  ? `"${v.length > 80 ? v.slice(0, 79) + '…' : v}"`
                  : typeof v === 'object'
                    ? JSON.stringify(v).slice(0, 80)
                    : String(v);
              return `${k}: ${vStr}\n`;
            })}
          </div>
        </Section>
      )}

      {!isError && result && (resultSuccess != null || resultError) && (
        <Section label="Result" darkMode={darkMode}>
          {resultError ? (
            <span className={darkMode ? 'text-rose-300' : 'text-rose-700'}>
              {String(resultError)}
            </span>
          ) : (
            <span className={darkMode ? 'text-emerald-300' : 'text-emerald-700'}>
              ✓ success
              {typeof result?.count === 'number' ? ` · ${result.count} items` : ''}
              {typeof result?.document_id === 'string'
                ? ` · ${result.document_id}`
                : ''}
              {typeof result?.peer_count === 'number'
                ? ` · ${result.peer_count} peers (${result.ok_count ?? 0} ok)`
                : ''}
            </span>
          )}
        </Section>
      )}
    </div>
  );
};

const LlmRoundHover: React.FC<{
  data: LlmRoundData;
  darkMode: boolean;
}> = ({ data, darkMode }) => {
  const model = shortenModel(data.modelUsed);
  const runState = data.runState ?? 'completed';
  const isSynth = data.isSynthesis;
  const isRunning = runState === 'running';
  const isErrorLike =
    runState === 'error' ||
    runState === 'timeout' ||
    runState === 'rate_limited' ||
    runState === 'budget_exceeded';

  // Synthesis-only meta — populated by the processor when the asst
  // message has landed. Lets the hover read as a "turn delivery"
  // summary instead of just round bookkeeping.
  const synthCost = isSynth ? fmtCost(data.turnTotalCostUsd) : null;
  const synthDuration = isSynth ? fmtDuration(data.turnDurationMs) : null;
  const synthHasStats =
    isSynth && (data.turnTotalTokens != null || synthCost || synthDuration);

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="font-bold text-[13px] leading-tight flex items-center gap-1.5">
          <span>{isSynth ? '⚡ Synthesis' : '⚙ LLM Round'}</span>
          {isSynth && !isRunning && !isErrorLike && (
            <span
              className={`text-[10px] font-semibold ${
                darkMode ? 'text-emerald-300' : 'text-emerald-600'
              }`}
            >
              ✓ delivered
            </span>
          )}
          <span
            className={`ml-auto font-mono font-normal text-[10px] ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            #{data.round}
          </span>
        </div>
        <div
          className={`text-[9px] mt-0.5 ${
            darkMode ? 'text-gray-500' : 'text-gray-400'
          }`}
        >
          {runState}
          {isSynth && data.turnTimestamp && ` · ${new Date(data.turnTimestamp).toLocaleTimeString()}`}
        </div>
      </div>

      {model && (
        <Section label="Model" mono darkMode={darkMode}>
          {model}
        </Section>
      )}

      {data.modelUsed && data.modelUsed !== model && (
        <Section label="Full id" mono darkMode={darkMode}>
          <span className="opacity-70 break-all">{data.modelUsed}</span>
        </Section>
      )}

      <div className="text-[10px] opacity-80">
        {data.toolCallCount > 0
          ? `→ ${data.toolCallCount} ${data.toolCallCount === 1 ? 'tool' : 'tools'} fanning out`
          : isSynth
            ? isRunning
              ? '⏳ writing the final answer'
              : '— last LLM call of the turn —'
            : 'no tools this round'}
      </div>

      {synthHasStats && (
        <div className="flex items-center gap-3 text-[10px] font-mono opacity-90">
          {data.turnTotalTokens != null && (
            <span>{data.turnTotalTokens.toLocaleString()} tok</span>
          )}
          {synthCost && <span>💰 {synthCost}</span>}
          {synthDuration && <span>⏱ {synthDuration}</span>}
        </div>
      )}

      {isSynth && data.turnAnswerPreview && (
        <Section label="Answer" darkMode={darkMode}>
          <MarkdownPreview text={data.turnAnswerPreview} maxLines={10} />
        </Section>
      )}
    </div>
  );
};

const TurnMarkerHover: React.FC<{
  data: TurnMarkerData;
  darkMode: boolean;
}> = ({ data, darkMode }) => {
  const isUser = data.role === 'user';
  const model = shortenModel(data.modelUsed);
  const content = data.fullContent ?? data.preview;
  const cost = fmtCost(data.totalCostUsd);

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="font-bold text-[13px] leading-tight">
          {isUser ? '👤 You' : '🤖 Assistant'}
        </div>
        {data.timestamp && (
          <div
            className={`text-[9px] font-mono mt-0.5 ${
              darkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
          >
            {new Date(data.timestamp).toLocaleString()}
          </div>
        )}
      </div>

      {!isUser && model && (
        <Section label="Model" mono darkMode={darkMode}>
          {model}
        </Section>
      )}

      {!isUser &&
        (cost || data.totalTokens != null || data.toolCallCount) && (
          <div className="flex items-center gap-3 text-[10px] font-mono opacity-90">
            {data.toolCallCount ? <span>🧰 {data.toolCallCount}</span> : null}
            {cost && <span>💰 {cost}</span>}
            {data.totalTokens != null && (
              <span>{data.totalTokens.toLocaleString()} tok</span>
            )}
          </div>
        )}

      {content && (
        <Section
          label={isUser ? 'Prompt' : 'Answer'}
          darkMode={darkMode}
        >
          <MarkdownPreview text={content} maxLines={isUser ? 8 : 12} />
        </Section>
      )}
    </div>
  );
};

export const DagNodeHoverContent: React.FC<{ data: ChatDagNodeData }> = ({
  data,
}) => {
  const { darkMode } = useTheme();

  switch (data.kind) {
    case 'peer':
      return <PeerHover data={data} darkMode={darkMode} />;
    case 'tool_call':
      return <ToolCallHover data={data} darkMode={darkMode} />;
    case 'llm_round':
      return <LlmRoundHover data={data} darkMode={darkMode} />;
    case 'turn_marker':
      return <TurnMarkerHover data={data} darkMode={darkMode} />;
    default:
      return null;
  }
};

export default DagNodeHoverContent;
